import _toArray from '@babel/runtime/helpers/esm/toArray';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import round from 'round';
import { extension, command, keyBinding, NamedShortcut, helper, MarkExtension, ExtensionTag, isElementDomNode, convertPixelsToDomUnit, ExtensionPriority, isString, omitExtraAttributes, joinStyles, clamp, isFunction, getTextSelection, getMarkRanges, parseSizeUnit, getFontSize } from '@remirror/core';
import { ExtensionFontSizeMessages } from '@remirror/messages';

var setFontSizeOptions = {
  icon: 'fontSize',
  description: _ref => {
    var t = _ref.t;
    return t(ExtensionFontSizeMessages.SET_DESCRIPTION);
  },
  label: _ref2 => {
    var t = _ref2.t;
    return t(ExtensionFontSizeMessages.SET_LABEL);
  }
};
var increaseFontSizeOptions = {
  icon: 'addLine',
  description: _ref3 => {
    var t = _ref3.t;
    return t(ExtensionFontSizeMessages.INCREASE_DESCRIPTION);
  },
  label: _ref4 => {
    var t = _ref4.t;
    return t(ExtensionFontSizeMessages.INCREASE_LABEL);
  }
};
var decreaseFontSizeOptions = {
  icon: 'subtractLine',
  description: _ref5 => {
    var t = _ref5.t;
    return t(ExtensionFontSizeMessages.DECREASE_DESCRIPTION);
  },
  label: _ref6 => {
    var t = _ref6.t;
    return t(ExtensionFontSizeMessages.DECREASE_LABEL);
  }
};
var FONT_SIZE_ATTRIBUTE = 'data-font-size-mark';

var _excluded = ["size"];

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _class, _class2;
/**
 * Add a font size to the selected text (or text within a specified range).
 */

var FontSizeExtension = (_dec = extension({
  defaultOptions: {
    defaultSize: '',
    unit: 'pt',
    increment: 1,
    max: 100,
    min: 1,
    roundingMultiple: 0.5
  },
  staticKeys: ['defaultSize']
}), _dec2 = command(setFontSizeOptions), _dec3 = command(increaseFontSizeOptions), _dec4 = command(decreaseFontSizeOptions), _dec5 = command(), _dec6 = keyBinding({
  shortcut: NamedShortcut.IncreaseFontSize,
  command: 'increaseFontSize'
}), _dec7 = keyBinding({
  shortcut: NamedShortcut.IncreaseFontSize,
  command: 'decreaseFontSize'
}), _dec8 = helper(), _dec9 = helper(), _dec(_class = (_class2 = class FontSizeExtension extends MarkExtension {
  get name() {
    return 'fontSize';
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.FontStyle];
  }

  createMarkSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({}, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        size: {
          default: this.options.defaultSize
        }
      }),
      parseDOM: [{
        tag: "span[".concat(FONT_SIZE_ATTRIBUTE, "]"),
        getAttrs: dom => {
          if (!isElementDomNode(dom)) {
            return;
          }

          var size = dom.getAttribute(FONT_SIZE_ATTRIBUTE);

          if (!size) {
            return;
          }

          size = "".concat(convertPixelsToDomUnit(size, this.options.unit, dom)).concat(this.options.unit);
          return _objectSpread(_objectSpread({}, extra.parse(dom)), {}, {
            size
          });
        }
      }, {
        // Get the color from the css style property. This is useful for pasted content.
        style: 'font-size',
        priority: ExtensionPriority.Low,
        getAttrs: size => {
          if (!isString(size)) {
            return;
          }

          size = this.getFontSize(size);
          return {
            size
          };
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: mark => {
        var _omitExtraAttributes = omitExtraAttributes(mark.attrs, extra),
            size = _omitExtraAttributes.size,
            other = _objectWithoutProperties(_omitExtraAttributes, _excluded);

        var extraAttrs = extra.dom(mark);
        var style = extraAttrs.style;
        var fontSize;

        if (size) {
          style = joinStyles({
            fontSize: this.getFontSize(size)
          }, style);
        }

        return ['span', _objectSpread(_objectSpread(_objectSpread({}, other), extraAttrs), {}, {
          style,
          [FONT_SIZE_ATTRIBUTE]: fontSize
        }), 0];
      }
    });
  }

  getFontSize(size) {
    var _this$store$view;

    var _this$options = this.options,
        unit = _this$options.unit,
        roundingMultiple = _this$options.roundingMultiple,
        max = _this$options.max,
        min = _this$options.min,
        defaultSize = _this$options.defaultSize;
    var fontSize = convertPixelsToDomUnit(size, unit, (_this$store$view = this.store.view) === null || _this$store$view === void 0 ? void 0 : _this$store$view.dom);

    if (Number.isNaN(fontSize)) {
      return defaultSize || '1rem';
    }

    var value = clamp({
      value: round(fontSize, roundingMultiple),
      max,
      min
    });
    return "".concat(value).concat(unit);
  }
  /**
   * Set the text size color value for the selected text.
   */


  setFontSize(size, options) {
    return this.store.commands.applyMark.original(this.type, // Store always as string. This removes the need to treat string vs number when using the value
    {
      size: String(size)
    }, options === null || options === void 0 ? void 0 : options.selection);
  }

  increaseFontSize(options) {
    var increment = this.options.increment;
    return props => {
      var _this$getFontSizeForS = this.getFontSizeForSelection(options === null || options === void 0 ? void 0 : options.selection),
          _this$getFontSizeForS2 = _slicedToArray(_this$getFontSizeForS, 1),
          parsedFontSize = _this$getFontSizeForS2[0];

      var _parsedFontSize = _slicedToArray(parsedFontSize, 1),
          size = _parsedFontSize[0];

      size += isFunction(increment) ? increment(parsedFontSize, 1) : increment;
      return this.setFontSize(size, options)(props);
    };
  }

  decreaseFontSize(options) {
    var increment = this.options.increment;
    return props => {
      var _this$getFontSizeForS3 = this.getFontSizeForSelection(options === null || options === void 0 ? void 0 : options.selection),
          _this$getFontSizeForS4 = _slicedToArray(_this$getFontSizeForS3, 1),
          parsedFontSize = _this$getFontSizeForS4[0];

      var _parsedFontSize2 = _slicedToArray(parsedFontSize, 1),
          size = _parsedFontSize2[0];

      size -= isFunction(increment) ? increment(parsedFontSize, -1) : increment;
      return this.setFontSize(size, options)(props);
    };
  }
  /**
   * Remove the size mark from the selection.
   */


  removeFontSize(options) {
    return this.store.commands.removeMark.original(_objectSpread({
      type: this.type
    }, options));
  }
  /**
   * Handle exiting the mark forwards.
   */


  increaseFontSizeShortcut(props) {
    return this.increaseFontSize()(props);
  }
  /**
   * Handle exiting the mark forwards.
   */


  decreaseFontSizeShortcut(props) {
    return this.decreaseFontSize()(props);
  }
  /**
   * Get the font size at the current selection (or provided custom selection).
   * Returns an array of parsed font size tuples if there are multiple sizes in
   * the non-empty selection.
   */


  getFontSizeForSelection(position) {
    var state = this.store.getState();
    var selection = getTextSelection(position !== null && position !== void 0 ? position : state.selection, state.doc);

    var _getMarkRanges = getMarkRanges(selection, this.type),
        _getMarkRanges2 = _toArray(_getMarkRanges),
        range = _getMarkRanges2[0],
        rest = _getMarkRanges2.slice(1);

    if (range) {
      return [parseSizeUnit(range.mark.attrs.size), ...rest.map(range => parseSizeUnit(range.mark.attrs.size))];
    }

    var _this$options2 = this.options,
        defaultSize = _this$options2.defaultSize,
        unit = _this$options2.unit;
    var parsedSize = [convertPixelsToDomUnit(defaultSize, unit), unit];
    return [parsedSize];
  }

  getFontSizeFromDom(position) {
    var state = this.store.getState();
    var selection = getTextSelection(position !== null && position !== void 0 ? position : state.selection, state.doc);
    var nodeAtPos = this.store.view.domAtPos(selection.from);
    var element = isElementDomNode(nodeAtPos.node) ? nodeAtPos.node : this.store.view.dom;
    return parseSizeUnit(getFontSize(element));
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "setFontSize", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "setFontSize"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "increaseFontSize", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "increaseFontSize"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "decreaseFontSize", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "decreaseFontSize"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "removeFontSize", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "removeFontSize"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "increaseFontSizeShortcut", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "increaseFontSizeShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "decreaseFontSizeShortcut", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "decreaseFontSizeShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getFontSizeForSelection", [_dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "getFontSizeForSelection"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getFontSizeFromDom", [_dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "getFontSizeFromDom"), _class2.prototype)), _class2)) || _class);

export { FontSizeExtension };
